import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, ShoppingBag, Wallet, Trophy, DollarSign, Briefcase, ClipboardList } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const Stat = ({ icon: Icon, label, value, accent = false, testid }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className={`glass rounded-xl p-5 ${accent ? "border-[#ff7a00]/40" : ""}`} data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-[#ff7a00]/10 border border-[#ff7a00]/30 flex items-center justify-center text-[#ff7a00]">
      <Icon size={18} />
    </div>
    <div className="mt-4 font-display text-2xl font-bold">{value}</div>
    <div className="text-xs text-[#8a8a8a] uppercase tracking-wider mt-1">{label}</div>
  </motion.div>
);

export const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [notifs, setNotifs] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/notifications").then((r) => setNotifs(r.data || []));
  }, []);

  if (!stats) return <div className="text-[#8a8a8a]" data-testid="admin-loading">Loading admin stats...</div>;
  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      <div>
        <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-1">Admin Console</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Overview</h1>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total Traders" value={stats.total_traders} testid="admin-stat-traders" />
        <Stat icon={ShoppingBag} label="Paid Orders" value={stats.paid_orders} testid="admin-stat-orders" />
        <Stat icon={Briefcase} label="Funded Accounts" value={stats.funded_accounts} testid="admin-stat-funded" />
        <Stat icon={Wallet} label="Pending Payouts" value={stats.pending_payouts} testid="admin-stat-pending-payouts" />
        <Stat icon={DollarSign} label="Revenue (USD)" value={`$${stats.revenue_usd.toLocaleString()}`} accent testid="admin-stat-revenue" />
        <Stat icon={DollarSign} label="Paid Out (USD)" value={`$${stats.total_paid_out.toLocaleString()}`} testid="admin-stat-paid-out" />
        <Stat icon={ClipboardList} label="Pending Assignment" value={stats.pending_assignment} testid="admin-stat-pending-assign" />
        <Stat icon={Trophy} label="Total Accounts" value={stats.total_accounts} testid="admin-stat-total-accounts" />
      </div>
      <div className="glass rounded-xl p-6" data-testid="admin-recent-notifs">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-semibold">Recent Activity</div>
          <Link to="/admin/orders" className="text-xs text-[#ff9a2f] hover:text-[#ff7a00]">All orders</Link>
        </div>
        {notifs.length === 0 && <div className="text-sm text-[#8a8a8a]">No new alerts.</div>}
        <div className="space-y-2">
          {notifs.slice(0, 10).map((n) => (
            <div key={n.id} className="flex items-center justify-between text-sm p-3 rounded-lg hover:bg-white/5 border border-transparent hover:border-[#ff7a00]/15">
              <div>
                <div className="text-[#f5f5f5] font-semibold">Paid order awaiting assignment</div>
                <div className="text-xs text-[#8a8a8a] mt-1">{n.user_email} · {n.challenge_name} · ${n.account_size?.toLocaleString()}</div>
              </div>
              <Badge className="bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30 uppercase text-[10px] tracking-widest">Assign</Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
